import { RefObject, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EventTypes } from '@cashier-integration/model';

import { selectPaymentMethod } from '@cashier/cashier-data-access';
import {
  CardDataSchema,
  CashierMessages,
  InputTypes,
  PaymentFormField,
} from '@cashier/cashier-model';
import { eventBus, EventFactory } from '@cashier/cashier-shared';

interface UseCashierDataForLeaderIframeParams {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  schema: CardDataSchema;
  iframeOrigin: string;
}

export const useCashierMessages = ({
  iframeRef,
  schema,
  iframeOrigin,
}: UseCashierDataForLeaderIframeParams): void => {
  const { preprocessor } = useSelector(selectPaymentMethod);

  const sendPostMessage = useCallback(
    (type: string, payload?: any) => {
      const iframe = iframeRef.current;

      if (!iframe?.contentWindow) return;

      iframe.contentWindow.postMessage(
        {
          type,
          payload,
        },
        iframeOrigin,
      );
    },
    [iframeOrigin, iframeRef],
  );

  const sendInitialData = useCallback(() => {
    sendPostMessage(CashierMessages.CashierData, {
      fields: Object.values(schema).map(
        (field: PaymentFormField) => field.type !== InputTypes.Checkbox,
      ),
      preprocessor,
    });
  }, [sendPostMessage, schema, preprocessor]);

  useEffect(() => {
    const iframeElement = iframeRef.current;

    if (!iframeElement) return;

    const handleLoad = () => {
      eventBus.emit(
        EventFactory.cashierMessage({
          task: CashierMessages.CashierData,
        }),
      );
    };

    iframeElement.addEventListener('load', handleLoad);
    return () => {
      iframeElement.removeEventListener('load', handleLoad);
    };
  }, [iframeRef]);

  useEffect(() => {
    return eventBus.on<{
      task?: string;
      processingResult?: unknown;
    }>(EventTypes.MESSAGE_TO_LEADER_IFRAME, (event) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;

      const { task, processingResult } = event.payload || {};

      if (task === CashierMessages.PostProcessSdkProcessorPayment) {
        sendPostMessage(CashierMessages.PostProcessSdkProcessorPayment, {
          ...(processingResult as Record<string, unknown>),
        });
        return;
      }

      if (
        task === CashierMessages.GetEncryptedPayload ||
        task === CashierMessages.LoadTokenizerV1
      ) {
        sendPostMessage(task);
        return;
      }

      if (task === CashierMessages.CashierData) {
        sendInitialData();
      }
    });
  }, [iframeRef, sendInitialData, sendPostMessage]);

  useEffect(() => {
    eventBus.emit(
      EventFactory.cashierMessage({
        task: CashierMessages.CashierData,
      }),
    );
  }, [preprocessor]);
};
